import * as React from 'react';
import { Responsive, Textarea, UIText3, useDeviceType } from '@cian/ui-kit';
import { debounce } from '@cian/newbuilding-utils';
import { useDispatch, useSelector } from 'react-redux';

import { getIsOpen } from '../../selectors/newAnswer';
import { setAnswerText } from '../../actions/newAnswer';
import { ANSWER_MAX_LENGTH } from '../../constants/reviews';
import { getAuthorNameById, getIsAuthorAnonymousById } from '../../selectors/common';
import * as styles from './AnswerTextContainer.css';
import { getError } from '../../selectors/newAnswer/getError';
import { setError } from '../../actions/newAnswer/answerErrorActions';

interface IAnswerTextContainerProps {
  insertDefaultValue?: boolean;
}

export const AnswerTextContainer = ({ insertDefaultValue = true }: IAnswerTextContainerProps) => {
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const openId = useSelector(getIsOpen);
  const author = useSelector(getAuthorNameById(openId));
  const isAnonymous = useSelector(getIsAuthorAnonymousById(openId));
  const error = useSelector(getError);

  const initialValue = !isAnonymous && insertDefaultValue ? `${author.trim()}, ` : '';

  const [value, setValue] = React.useState(initialValue);
  const [counter, setCounter] = React.useState(initialValue.length);

  const setAnswerTextDebounced = debounce(200, nextValue => dispatch(setAnswerText(nextValue)));

  const onChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLTextAreaElement>, nextValue: string) => {
      const counter = nextValue.length;
      if (error) {
        dispatch(setError(''));
      }
      if (counter <= ANSWER_MAX_LENGTH) {
        setCounter(nextValue.length);
        setValue(nextValue);
        setAnswerTextDebounced(nextValue);
      }
    },
    [setValue, setAnswerTextDebounced],
  );

  const counterText = `${counter}/${ANSWER_MAX_LENGTH}`;

  return (
    <div className={styles['root']}>
      <Textarea
        data-testId="AnswerTextContainer"
        onChange={onChange}
        value={value}
        minRows={isDesktop ? 6 : 10}
        maxRows={15}
        width="100%"
        invalid={!!error}
      />
      <Responsive desktop>
        {error ? (
          <div className={styles['error']}>
            <UIText3>{error}</UIText3>
          </div>
        ) : (
          <div className={styles['counter']}>
            <UIText3 color="gray40_100">{counterText}</UIText3>
          </div>
        )}
      </Responsive>
      <Responsive phone tablet>
        <div className={styles['counter']}>
          <UIText3 color="gray40_100">{counterText}</UIText3>
        </div>
      </Responsive>
    </div>
  );
};
